import React, { useMemo } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryOps } from "system/query/Query";
import { QueryInfo } from "system/query/QueryInfo";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryValueInput } from "../dialogParts/QueryValueInput";
import { Box, Button, IconButton, Pagination } from "@mui/material";
import { DeleteForever, AddBox, LibraryAdd} from '@mui/icons-material';
import { BulkTextInputDialog } from "components/dialogs/BulkTextInputDialog";

export interface QueryMultiValueInputProps {
  queryInfo: QueryInfo;

  fields: string[];

  values: any[];
  setValues: (values: any[]) => void;

  labels: string[];
  setLabels: (labels: string[]) => void;
}

const getLabelText = (type: string | undefined) => {
  const {i18n} = useTranslation();
  return type === "integer" ? i18n.t("query-form.value-integer") :
         type === "keyword" || type === "keywords" ? i18n.t("query-form.value-keyword") :
         i18n.t("query-form.value");
};

export function QueryMultiValueInput(props: QueryMultiValueInputProps): React.JSX.Element {
  const {i18n} = useTranslation();

  const {fields, values, setValues, labels, setLabels} = props;

  const [defaultValue, setDefaultValue] = useState<any>("");

  const totalValues = values.length;
  const rowsPerPage = 10;
  const isPaginated = totalValues > rowsPerPage;
  const [currentValuesPage, setCurrentValuesPage] = useState<number>(1);

  const [bulkValueInputIsOpen, setBulkValueInputIsOpen] = useState<boolean>(false);

  const firstField = useMemo(() => {
    return props.queryInfo.fields.find(field => field.id === fields?.[0]);
  }, [fields, props.queryInfo.fields]);

  const labelText = getLabelText(firstField?.type);

  useEffect(() => {
    const fieldType = firstField?.type;
    if (!values.length) {
      if (fieldType === "integer" || fieldType === "decimal") {
        setValues([0]);
        setDefaultValue(0);
      } else if (fieldType === "boolean") {
        setValues([true]);
        setDefaultValue(true);
      } else if (fieldType === "text") {
        setValues([""]);
        setDefaultValue("");
      } else if (firstField?.options?.[0]?.id) {
        setValues([firstField.options[0]?.id]);
        setLabels([firstField.options[0]?.label]);
        setDefaultValue(null);
      } else {
        setValues([""]);
        setDefaultValue("");
      }
    }
  }, [values.length, props.queryInfo.fields, fields, firstField?.type]);

  useEffect(() => {
    if (currentValuesPage > Math.ceil(totalValues / rowsPerPage)) {
      setCurrentValuesPage(Math.ceil(totalValues / rowsPerPage) || 1);
    }
  }, [totalValues, currentValuesPage]);

  const fieldValuesPage = useMemo(() => {
    let displayedValues = values;
    if (isPaginated) {
      displayedValues = values.slice((currentValuesPage - 1) * rowsPerPage, currentValuesPage * rowsPerPage);
    }

    return displayedValues.map((value, map_index) => {
      const index = map_index + ((currentValuesPage - 1) * rowsPerPage);

      let currentLabel = value;
      if (labels.length > index) {
        currentLabel = labels[index];
      }

      const isLastValue = index + currentValuesPage * rowsPerPage === values.length - 1;

      return (
        // Container box, should have a right column for buttons and an adjustable left column for the value
        <Box key={`QueryMultiValueInput_${index}`} sx={{ display: "grid", gridTemplateColumns: "1fr auto", gridTemplateRows: "auto", mb: 1 }}>
          <Box sx={{ gridRow: "1", gridColumn: "1", mr: 1, mb: firstField?.type === "timestamp" ? 1 : 0 }}>
            <QueryValueInput
              queryInfo={props.queryInfo}
              selectedField={firstField}
              value={value}
              label={currentLabel}
              isInline={true}
              isFocused={isLastValue}
              queryOp={queryOps.contains}
              keyWasPressed={(event: any) => {
                if ((event.key === "Tab" || event.key === "Enter") && index === values.length - 1) {
                  event.preventDefault();
                  const newValues = [...values];
                  newValues.push(defaultValue);
                  setCurrentValuesPage(Math.ceil(newValues.length / rowsPerPage));
                  setValues(newValues);
                // if the user is on the last value for a page and presses enter or tab, go to the next page
                } else if ((event.key === "Tab" || event.key === "Enter") &&
                  map_index === rowsPerPage - 1 && currentValuesPage < Math.ceil(totalValues / rowsPerPage)) {
                  event.preventDefault();
                  setCurrentValuesPage(currentValuesPage + 1);
                }
                if (event.key === "Backspace" && value === null) {
                  event.preventDefault();
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValues(newValues);
                }
              }}
              setValue={(newValue: any, newLabel?:string) => {
                const newValues = [...values];
                newValues[index] = newValue;
                setValues(newValues);
                if (newLabel) {
                  const newLabels = [...labels];
                  newLabels[index] = newLabel;
                  setLabels(newLabels);
                }
              }}
              setLabel={(newLabel: string) => {
                const newLabels = labels ? [...labels] : [];
                newLabels[index] = newLabel;
                setLabels(newLabels);
              }}
            />
          </Box>
            {(firstField?.options && firstField?.options?.length === 0)
              ? <></>
              : <Box sx={{ gridRow: "1", gridColumn: "2", alignContent: "center" }}>
                  <IconButton
                    onClick={() => {
                      const newValues = [...values];
                      const newLabels = [...labels];
                      newValues.splice(index, 1);
                      newLabels.splice(index, 1);
                      setValues(newValues);
                      setLabels(newLabels);
                    }}>
                    <DeleteForever />
                  </IconButton>
                </Box>
            }
        </Box>
      );
    });
  }, [values, currentValuesPage, labels, firstField, props.queryInfo]);

  return (
    <>
      {firstField?.type &&
        <>
          <SimpleFormLabel>{labelText}</SimpleFormLabel>
          {fieldValuesPage}
          {(firstField?.options && firstField?.options?.length === 0)
            ? <></>
            : <Box sx={{textAlign: "center"}}>
                {firstField.suggestions &&
                  <Button
                    startIcon={<LibraryAdd />}
                    onClick={() => setBulkValueInputIsOpen(true)}
                  >
                    {i18n.t("query-form.bulk-add-values")}
                  </Button>
                }
                <Button
                  sx={{ mt: 1, mb: 1 }}
                  variant="contained"
                  startIcon={<AddBox />}
                  onClick={() => {
                    // Add a new value to the list, then focus on it
                    const newValues = [...values];
                    newValues.push(defaultValue);
                    setCurrentValuesPage(Math.ceil(newValues.length / rowsPerPage));
                    setValues(newValues);
                  }}>
                  {i18n.t("query-form.add-value")}
                </Button>
                <br/>
                {totalValues > rowsPerPage &&
                  <Pagination
                    count={Math.ceil(totalValues / rowsPerPage)}
                    sx={{ display:"inline-block" }}
                    page={currentValuesPage}
                    onChange={(_event: React.ChangeEvent<unknown>, page: number) => setCurrentValuesPage(page)}
                    shape="rounded"
                    size="small"
                    showFirstButton
                    showLastButton />
                }
              </Box>
          }
        </>
      }
      {bulkValueInputIsOpen &&
        <BulkTextInputDialog
          initialValues={values}
          onClose={() => setBulkValueInputIsOpen(false)}
          onSubmit={(newValues: string[]) => {
            setValues([...newValues]);
            setLabels([...newValues]);
            setBulkValueInputIsOpen(false);
          }}
        />
      }
    </>
  );
}
